/**
 * Wait for a card-reader tap to land — ONE polling loop for every surface.
 *
 * The POS sale, the member Record Payment modal and the enrollment wizard
 * all start a charge on their own endpoint and then need the same thing:
 * ask GET /api/terminal/charges/:id until it is no longer "waiting", and
 * hand back the terminal answer. Keeping the loop here means the cadence,
 * the timeout and the "charged but not recorded — do NOT retry" case are
 * decided once.
 */
import { getReaderCharge, ReaderChargeResponse } from "../api/terminalAPI";

export interface WaitForReaderOptions {
  /** Poll interval in ms. Readers report within a second or two of the tap. */
  intervalMs?: number;
  /** Give up after this long (ms). The reader itself times out well before. */
  timeoutMs?: number;
  /** Cancel the wait (the loop stops; the charge itself is NOT canceled). */
  signal?: AbortSignal;
  /** Called on each non-final poll with the reader's current line. */
  onTick?: (message: string | null) => void;
}

export type ReaderChargeOutcome =
  | ({ outcome: "succeeded" } & ReaderChargeResponse)
  | ({ outcome: "failed" } & ReaderChargeResponse)
  | ({ outcome: "canceled" } & ReaderChargeResponse)
  | { outcome: "aborted" }
  | { outcome: "timeout" }
  /** The card WAS charged but the platform could not save the payment. */
  | ({ outcome: "charged_not_recorded" } & ReaderChargeResponse);

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      clearTimeout(t);
      resolve();
    });
  });

export async function waitForReaderCharge(
  token: string,
  paymentIntentId: string,
  opts: WaitForReaderOptions = {}
): Promise<ReaderChargeOutcome> {
  const intervalMs = opts.intervalMs ?? 1500;
  const timeoutMs = opts.timeoutMs ?? 3 * 60 * 1000;
  const started = Date.now();

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (opts.signal?.aborted) return { outcome: "aborted" };

    const res = await getReaderCharge(token, paymentIntentId);

    if (!res.success && res.charged) {
      return { outcome: "charged_not_recorded", ...res };
    }
    if (res.success) {
      if (res.status === "succeeded") return { outcome: "succeeded", ...res };
      if (res.status === "failed") return { outcome: "failed", ...res };
      if (res.status === "canceled") return { outcome: "canceled", ...res };
      opts.onTick?.(res.message ?? null);
    } else {
      // A transient network failure is not a verdict on the tap. Keep polling.
      opts.onTick?.(res.error ?? null);
    }

    if (Date.now() - started > timeoutMs) return { outcome: "timeout" };
    await sleep(intervalMs, opts.signal);
  }
}

/** The line staff should read for a finished wait. */
export function describeReaderOutcome(o: ReaderChargeOutcome): string {
  switch (o.outcome) {
    case "succeeded":
      return o.card?.saved ? "Payment taken. The card was saved to the account." : "Payment taken.";
    case "failed":
      return o.message || "The card was declined.";
    case "canceled":
      return o.message || "The payment was canceled.";
    case "charged_not_recorded":
      return (
        o.error ||
        `The card was charged but the payment could not be saved. Do NOT charge again — record it manually and reference Stripe payment ${o.paymentIntentId || ""}.`
      );
    case "timeout":
      return "No tap was received. Check the reader and try again.";
    case "aborted":
      return "Stopped waiting for the reader.";
    default:
      return "";
  }
}
